import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  clearDefaultConcurrencyLimit,
  clearWorkspaceConcurrencyLimit,
  readOrganizationConcurrency,
  setDefaultConcurrencyLimit,
  setWorkspaceConcurrencyLimit,
} from '@/lib/api/generated/api';
import type { OrganizationConcurrencyView } from '@/lib/api/generated/model';
import { workspaceKeys } from '@/features/workspaces';

import { organizationKeys } from '../queryKeys';

/** While anything waits, often enough to see queued runs start after a limit is raised. */
const QUEUE_REFRESH_MS = 5_000;

/**
 * Every workspace's concurrency limit and load, in one read (spec 115 US3, service
 * spec 110).
 *
 * `null` from a service that predates the read (404): the section is then simply not
 * shown, rather than failing a page whose spend limits still work.
 */
export function useOrganizationConcurrency() {
  return useQuery<OrganizationConcurrencyView | null>({
    queryKey: organizationKeys.concurrency(),
    queryFn: async ({ signal }) => {
      const response = await readOrganizationConcurrency({ signal });
      if (response.status === 200) return response.data;
      if ((response.status as number) === 404) return null;
      throw new Error('Could not read how many runs each workspace may run.');
    },
    // Refreshed while runs wait, so raising a limit visibly starts them (US3
    // scenario 3); at rest it costs nothing.
    refetchInterval: (query) =>
      query.state.data?.workspaces.some((row) => row.queued > 0)
        ? QUEUE_REFRESH_MS
        : false,
    refetchOnWindowFocus: false,
  });
}

function limitError(status: number, ceiling: number): Error {
  if (status === 403) {
    return new Error(
      'Only an organization administrator can change how many runs may go at once.',
    );
  }
  if (status === 422) {
    return new Error(`A whole number from 1 to ${ceiling}`);
  }
  if (status === 404) {
    return new Error('That workspace no longer exists.');
  }
  return new Error('Could not save the limit.');
}

/**
 * A limit change moves every workspace that resolves through it, and changes the
 * limit each board's queue notice names — so the organization read and every
 * workspace read are discarded, never patched.
 */
function useInvalidateConcurrency() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: organizationKeys.concurrency(),
      }),
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all() }),
    ]);
  };
}

export interface ConcurrencyLimitTarget {
  /** The workspace to override, or `null` for the organization default. */
  workspaceId: string | null;
}

export function useSetConcurrencyLimit(ceiling: number) {
  const invalidate = useInvalidateConcurrency();
  return useMutation({
    mutationFn: async ({
      workspaceId,
      value,
    }: ConcurrencyLimitTarget & { value: number }) => {
      const response =
        workspaceId === null
          ? await setDefaultConcurrencyLimit({ value })
          : await setWorkspaceConcurrencyLimit(workspaceId, { value });
      if (response.status !== 200) {
        throw limitError(response.status, ceiling);
      }
    },
    onSuccess: invalidate,
  });
}

export function useClearConcurrencyLimit(ceiling: number) {
  const invalidate = useInvalidateConcurrency();
  return useMutation({
    mutationFn: async ({ workspaceId }: ConcurrencyLimitTarget) => {
      const response =
        workspaceId === null
          ? await clearDefaultConcurrencyLimit()
          : await clearWorkspaceConcurrencyLimit(workspaceId);
      if (response.status !== 204) {
        throw limitError(response.status, ceiling);
      }
    },
    onSuccess: invalidate,
  });
}
